import React, { useEffect, useState } from 'react';
import { LayoutTemplate, X, Loader2, AlertCircle } from 'lucide-react';
import { siteTemplatesApi } from '../../../api/client';
import type { SiteTemplate } from '../../../schemas/validation';
import { TemplateMiniPreview } from '../../TemplateMiniPreview';

interface ImportLayoutStepProps {
  onSelect: (template: { id: string; name: string }) => void;
  onClose: () => void;
  title?: string;
  intro?: string;
}

function isListedLayout(template: SiteTemplate): boolean {
  return template.isPublic === true;
}

const DEFAULT_INTRO =
  "Great! I can do that for you. I'll get you most of the way there, and then you can finalize the site. To start, select the template you'd like.";

export const ImportLayoutStep: React.FC<ImportLayoutStepProps> = ({
  onSelect,
  onClose,
  title = 'Import an existing website',
  intro = DEFAULT_INTRO,
}) => {
  const [templates, setTemplates] = useState<SiteTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    siteTemplatesApi
      .browseTemplates({ includePublic: true })
      .then(res => {
        if (cancelled) return;
        setTemplates((res.templates || []).filter(isListedLayout));
      })
      .catch(() => {
        if (!cancelled) setError('Could not load templates. Try again.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="flex flex-col h-full bg-slate-900 text-white">
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
        <div className="flex items-center gap-3">
          <LayoutTemplate className="w-5 h-5 text-orange-400" />
          <h2 className="text-lg font-semibold">{title}</h2>
        </div>
        <button
          onClick={onClose}
          className="p-2 rounded-lg hover:bg-slate-800 transition-colors"
          aria-label="Close"
        >
          <X size={18} />
        </button>
      </div>

      <div className="flex-1 px-6 py-6 overflow-y-auto">
        <p className="text-slate-300 mb-6">{intro}</p>

        {loading && (
          <div className="flex items-center justify-center gap-2 py-16 text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin" />
            Loading templates…
          </div>
        )}

        {error && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/30">
            <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-300">{error}</p>
          </div>
        )}

        {!loading && !error && templates.length === 0 && (
          <p className="text-sm text-slate-400">No public templates are available yet.</p>
        )}

        {!loading && templates.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="import-layout-grid">
            {templates.map(template => (
              <button
                key={template.id}
                type="button"
                onClick={() => onSelect({ id: template.id, name: template.name })}
                className="text-left rounded-xl overflow-hidden border border-slate-700 bg-slate-800/60 hover:border-orange-500/60 hover:bg-slate-800 transition-colors"
              >
                <div className="aspect-video relative overflow-hidden bg-slate-950">
                  <TemplateMiniPreview template={template} />
                  <span className="absolute bottom-2 left-2 bg-black/60 text-white text-xs px-2 py-0.5 rounded">
                    {template.siteData?.pages?.length || 0} pages
                  </span>
                </div>
                <div className="p-3">
                  <div className="font-semibold text-white">{template.name}</div>
                  {template.description && (
                    <p className="mt-1 text-xs text-slate-400 line-clamp-2">
                      {template.description}
                    </p>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
